import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  HotlinePresence,
  HotlineStatusCounts,
  HotlineTrackedCall,
  HotlineWorkSegment,
  HotlineWorkShift,
  HotlinePresenceCounts,
  getHotlinePresence,
  getHotlineStatusSummary,
  getHotlineTeamCalls,
  getHotlineTrackedCalls,
  getHotlineWorkTimeline,
} from "../../services/hotlineQueueApi";

const ROLE_LABEL: Record<string, string> = {
  ADMIN: "Admin",
  FBDE: "Franchise Manager",
  FBDE_ADMIN: "Franchise Manager Admin",
  SHM: "Senior Hiring Manager",
  SHM_ADMIN: "Senior Hiring Manager Admin",
  HM: "Freelance Recruiter",
  HR: "Intern Recruiter",
  BDE: "Business Development",
};

const PRESENCE_LABEL: Record<string, string> = {
  on_call: "On call",
  break: "Break",
  working: "Working",
  off: "Off",
};

const ACTIVITY_LABEL: Record<string, string> = {
  idle: "Idle",
  dialing: "Dialing",
  talk: "Talk",
  afterCall: "After call",
  review: "Review",
  breakTime: "Break",
};

export const CALL_STATUS_COLUMNS = [
  ["interested", "Interested"],
  ["notInterested", "Not Interested"],
  ["notReachable", "Not Reachable"],
  ["ringingNotReachable", "Ringing Not Reachable"],
  ["callbackRequested", "Call Back"],
  ["schedule", "Schedule"],
  ["inProgression", "In Progression"],
  ["notRelevant", "Not Relevant"],
  ["wrongNumber", "Incorrect Number"],
  ["dnd", "DND"],
  ["switchedOff", "Switched Off"],
  ["salaryIssue", "Salary Issue"],
  ["locationIssue", "Location Issue"],
  ["alreadyEmployed", "Already Employed"],
] as const;

function todayInput() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function rangeFromInputs(fromDay: string, toDay: string) {
  const [fromYear, fromMonth, fromDate] = fromDay.split("-").map(Number);
  const [toYear, toMonth, toDate] = toDay.split("-").map(Number);
  const from = new Date(fromYear, fromMonth - 1, fromDate);
  const to = new Date(toYear, toMonth - 1, toDate + 1);
  return { from: from.toISOString(), to: to.toISOString() };
}

function formatDuration(seconds: number) {
  const total = Math.max(0, Math.round(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function reasonLabel(reason: string | null) {
  if (!reason) return "";
  if (reason.startsWith("No activity")) return "No activity";
  return reason;
}

function segmentLength(segment: HotlineWorkSegment) {
  const start = new Date(segment.startedAt).getTime();
  const end = segment.endedAt ? new Date(segment.endedAt).getTime() : Date.now();
  return Math.max(0, Math.round((end - start) / 1000));
}

function formatWhen(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatClock(value: string) {
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function sameDay(left: string, right: string) {
  const a = new Date(left);
  const b = new Date(right);
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const PAGE_SIZE = 20;

function TablePager({
  page,
  total,
  pageSize = PAGE_SIZE,
  onPage,
}: {
  page: number;
  total: number;
  pageSize?: number;
  onPage: (page: number) => void;
}) {
  if (total === 0) return null;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        className="rounded-lg border border-slate-200 px-2.5 py-1 font-medium text-slate-800 disabled:opacity-40"
      >
        Previous
      </button>
      <span>
        {page} / {pageCount}
      </span>
      <button
        type="button"
        disabled={page >= pageCount}
        onClick={() => onPage(page + 1)}
        className="rounded-lg border border-slate-200 px-2.5 py-1 font-medium text-slate-800 disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

function errorMessage(error: unknown) {
  const body = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return body?.message || "Tracking could not be loaded.";
}

export const HotlineLiveTracking: React.FC = () => {
  const [presence, setPresence] = useState<HotlinePresence[]>([]);
  const [presencePage, setPresencePage] = useState(1);
  const [presenceTotal, setPresenceTotal] = useState(0);
  const [presenceCounts, setPresenceCounts] = useState<HotlinePresenceCounts>({
    on_call: 0,
    break: 0,
    working: 0,
    off: 0,
    totalOnline: 0,
  });
  const [presenceLoading, setPresenceLoading] = useState(true);
  const [presenceQuery, setPresenceQuery] = useState("");
  const [presenceSort, setPresenceSort] = useState<"status" | "work" | "calls">("status");
  const [presenceLimit, setPresenceLimit] = useState(PAGE_SIZE);
  const presenceQueryRef = useRef("");
  const presenceSortRef = useRef<"status" | "work" | "calls">("status");
  const presenceLimitRef = useRef(PAGE_SIZE);
  const skipPresenceSearch = useRef(true);
  const [selectedId, setSelectedId] = useState("");
  const [shifts, setShifts] = useState<HotlineWorkShift[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const loadPresence = useCallback(async (
    page = 1,
    search = presenceQueryRef.current,
    sort = presenceSortRef.current,
    limit = presenceLimitRef.current,
  ) => {
    setPresenceLoading(true);
    try {
      const result = await getHotlinePresence(page, search, sort, limit);
      setPresence(result.items);
      setPresencePage(result.page);
      setPresenceTotal(result.total);
      setPresenceCounts(result.counts);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setPresenceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipPresenceSearch.current) {
      skipPresenceSearch.current = false;
      return;
    }
    const handle = window.setTimeout(() => {
      presenceQueryRef.current = presenceQuery.trim();
      loadPresence(1, presenceQueryRef.current);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [presenceQuery, loadPresence]);

  useEffect(() => {
    presenceSortRef.current = presenceSort;
    loadPresence(1, presenceQueryRef.current, presenceSort);
  }, [presenceSort, loadPresence]);

  const openTimeline = async (person: HotlinePresence) => {
    setSelectedId(person.recruiterId);
    setTimelineLoading(true);
    try {
      const rows = await getHotlineWorkTimeline(person.recruiterId);
      setShifts(rows.slice().reverse());
    } catch (error) {
      toast.error(errorMessage(error));
      setShifts([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const selected = presence.find((person) => person.recruiterId === selectedId);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">
            Working now
            <span className="ml-3 text-xs font-medium text-slate-500">
              Online {presenceCounts.on_call + presenceCounts.working} · Offline{" "}
              {presenceCounts.break + presenceCounts.off} · Total online {presenceCounts.totalOnline}
            </span>
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Today’s shift for everyone in this branch. Open a row for the last 14 days.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={presenceSort}
            onChange={(event) => setPresenceSort(event.target.value as "status" | "work" | "calls")}
            aria-label="Sort this branch"
            className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
          >
            <option value="status">Sort by status</option>
            <option value="work">Sort by work time</option>
            <option value="calls">Sort by calls</option>
          </select>
          <input
            type="search"
            value={presenceQuery}
            onChange={(event) => setPresenceQuery(event.target.value)}
            placeholder="Search this branch"
            className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
          />
          <button
            type="button"
            onClick={() => loadPresence(presencePage)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-800"
          >
            Refresh
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Person</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Ended</th>
              <th className="px-4 py-3">Today</th>
              <th className="px-4 py-3">Calling</th>
              <th className="px-4 py-3">Calls</th>
            </tr>
          </thead>
          <tbody>
            {presence.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  {presenceLoading
                    ? "Loading working time…"
                    : presenceQuery.trim()
                      ? "No one matches that search."
                      : "No one is in this branch yet."}
                </td>
              </tr>
            ) : (
              presence.map((person) => {
                const active = person.recruiterId === selectedId;
                return (
                  <tr
                    key={person.recruiterId}
                    className={`cursor-pointer border-t border-slate-100 ${
                      active ? "bg-[#FFF1EC]" : "hover:bg-slate-50"
                    }`}
                    onClick={() => openTimeline(person)}
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">{person.name}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                            person.status === "off"
                              ? "bg-red-500"
                              : person.status === "break"
                                ? "bg-orange-500"
                                : "bg-green-500"
                          }`}
                          aria-hidden
                        />
                        <span>
                          {PRESENCE_LABEL[person.status] || person.status}
                          {person.status === "break" && reasonLabel(person.breakReason)
                            ? ` · ${reasonLabel(person.breakReason)}`
                            : ""}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatWhen(person.startedAt)}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {person.endedAt ? formatWhen(person.endedAt) : person.startedAt ? "Open" : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDuration(person.todaySeconds)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDuration(person.callingSeconds)}</td>
                    <td className="px-4 py-3 text-slate-600">{person.callCount}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {selected && (
        <div className="border-t border-slate-200 px-4 py-4">
          <h3 className="text-sm font-semibold text-slate-800">{selected.name} · last 14 days</h3>
          {timelineLoading ? (
            <p className="mt-2 text-sm text-slate-500">Loading timeline…</p>
          ) : shifts.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">No shift in the last 14 days.</p>
          ) : (
            <div className="mt-3 max-h-80 overflow-auto rounded-lg border border-slate-200">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Activity</th>
                    <th className="px-3 py-2">Started</th>
                    <th className="px-3 py-2">Ended</th>
                    <th className="px-3 py-2">Length</th>
                    <th className="px-3 py-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift) => (
                    <React.Fragment key={shift.id}>
                      <tr className="border-t border-slate-200 bg-slate-50">
                        <td colSpan={5} className="px-3 py-2 text-xs font-medium text-slate-600">
                          Shift · {formatWhen(shift.startedAt)} –{" "}
                          {shift.endedAt ? formatWhen(shift.endedAt) : "Open"}
                        </td>
                      </tr>
                      {shift.segments.map((segment) => (
                        <tr key={segment.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-medium text-slate-900">
                            {ACTIVITY_LABEL[segment.activity] || segment.activity}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                            {sameDay(segment.startedAt, shift.startedAt)
                              ? formatClock(segment.startedAt)
                              : formatWhen(segment.startedAt)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                            {segment.endedAt
                              ? sameDay(segment.endedAt, shift.startedAt)
                                ? formatClock(segment.endedAt)
                                : formatWhen(segment.endedAt)
                              : "Now"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                            {formatDuration(segmentLength(segment))}
                          </td>
                          <td className="px-3 py-2 text-slate-500">
                            {reasonLabel(segment.breakReason) || "—"}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {presenceTotal > 0 && (
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-4 py-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            Per page
            <select
              value={presenceLimit}
              aria-label="Rows per page"
              onChange={(event) => {
                const next = Number(event.target.value);
                presenceLimitRef.current = next;
                setPresenceLimit(next);
                loadPresence(1, presenceQueryRef.current, presenceSortRef.current, next);
              }}
              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <TablePager
            page={presencePage}
            total={presenceTotal}
            pageSize={presenceLimit}
            onPage={loadPresence}
          />
        </div>
      )}
    </section>
  );
};

const HotlineTracking: React.FC = () => {
  const [fromDay, setFromDay] = useState(todayInput);
  const [toDay, setToDay] = useState(todayInput);
  const [summary, setSummary] = useState<HotlineStatusCounts[]>([]);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryQuery, setSummaryQuery] = useState("");
  const summaryQueryRef = useRef("");
  const summaryRangeRef = useRef({ from: todayInput(), to: todayInput() });
  summaryRangeRef.current = { from: fromDay, to: toDay };
  const skipSummarySearch = useRef(true);
  const [statusPerson, setStatusPerson] = useState<HotlineStatusCounts | null>(null);
  const [calls, setCalls] = useState<HotlineTrackedCall[]>([]);
  const [callsPage, setCallsPage] = useState(1);
  const [callsHasMore, setCallsHasMore] = useState(false);
  const [callsLoading, setCallsLoading] = useState(false);
  const [callStatus, setCallStatus] = useState("");

  const loadSummary = useCallback(async (
    from: string,
    to: string,
    page = 1,
    resetDetail = false,
    search = summaryQueryRef.current,
  ) => {
    const range = rangeFromInputs(from, to);
    if (new Date(range.to) <= new Date(range.from)) {
      toast.error("The end date has to be on or after the start date.");
      return;
    }
    setSummaryLoading(true);
    if (resetDetail) {
      setStatusPerson(null);
      setCalls([]);
    }
    try {
      const result = await getHotlineStatusSummary(range.from, range.to, page, search);
      setSummary(result.items);
      setSummaryPage(result.page);
      setSummaryTotal(result.total);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipSummarySearch.current) {
      skipSummarySearch.current = false;
      return;
    }
    const handle = window.setTimeout(() => {
      summaryQueryRef.current = summaryQuery.trim();
      const range = summaryRangeRef.current;
      loadSummary(range.from, range.to, 1, true, summaryQueryRef.current);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [summaryQuery, loadSummary]);

  useEffect(() => {
    loadSummary(todayInput(), todayInput());
  }, [loadSummary]);

  const openCalls = async (person: HotlineStatusCounts, page = 1, status = callStatus) => {
    const range = rangeFromInputs(fromDay, toDay);
    setStatusPerson(person);
    setCallsLoading(true);
    try {
      const result = await getHotlineTrackedCalls({
        recruiterId: person.recruiterId,
        from: range.from,
        to: range.to,
        page,
        disposition: status || undefined,
      });
      setCalls((current) => (page === 1 ? result.items : [...current, ...result.items]));
      setCallsPage(page);
      setCallsHasMore(result.hasMore);
    } catch (error) {
      toast.error(errorMessage(error));
      if (page === 1) setCalls([]);
    } finally {
      setCallsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Call statuses</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Saved call statuses for everyone in this branch. Open a row to see the calls.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <input
              type="search"
              value={summaryQuery}
              onChange={(event) => setSummaryQuery(event.target.value)}
              placeholder="Search this branch"
              className="w-56 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
            />
            <TablePager
              page={summaryPage}
              total={summaryTotal}
              onPage={(page) => loadSummary(fromDay, toDay, page)}
            />
            <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              loadSummary(fromDay, toDay, 1, true);
            }}
          >
            <label className="text-xs text-slate-500">
              From
              <input
                type="date"
                value={fromDay}
                onChange={(event) => setFromDay(event.target.value)}
                className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
            <label className="text-xs text-slate-500">
              To
              <input
                type="date"
                value={toDay}
                onChange={(event) => setToDay(event.target.value)}
                className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
              />
            </label>
            <button
              type="submit"
              className="rounded-lg bg-[#EC6E51] px-3 py-1.5 text-sm font-medium text-white"
            >
              Apply
            </button>
          </form>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">Attempts</th>
                {CALL_STATUS_COLUMNS.map(([key, label]) => (
                  <th key={key} className="px-4 py-3">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-8 text-center text-slate-500">
                    {summaryLoading
                      ? "Loading statuses…"
                      : summaryQuery.trim()
                        ? "No one matches that search."
                        : "No one is in this branch yet."}
                  </td>
                </tr>
              ) : (
                summary.map((person) => {
                  const active = statusPerson?.recruiterId === person.recruiterId;
                  return (
                    <tr
                      key={person.recruiterId}
                      className={`cursor-pointer border-t border-slate-100 ${
                        active ? "bg-[#FFF1EC]" : "hover:bg-slate-50"
                      }`}
                      onClick={() => openCalls(person)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{person.name}</div>
                        <div className="text-xs text-slate-500">
                          {ROLE_LABEL[person.role] || person.role}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{person.attempts}</td>
                      {CALL_STATUS_COLUMNS.map(([key]) => (
                        <td key={key} className="px-4 py-3 text-slate-600">
                          {person.statuses[key] ?? 0}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {statusPerson && (
          <div className="border-t border-slate-200 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-800">{statusPerson.name} · calls</h3>
              <label className="text-xs text-slate-500">
                Status
                <select
                  value={callStatus}
                  onChange={(event) => {
                    const next = event.target.value;
                    setCallStatus(next);
                    openCalls(statusPerson, 1, next);
                  }}
                  className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-[#EC6E51]"
                >
                  <option value="">All statuses</option>
                  {CALL_STATUS_COLUMNS.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {calls.length === 0 && !callsLoading ? (
              <p className="mt-2 text-sm text-slate-500">
                {callStatus
                  ? "No saved calls with that status in this period."
                  : "No saved calls in this period."}
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Time</th>
                      <th className="px-3 py-2">Candidate</th>
                      <th className="px-3 py-2">Job</th>
                      <th className="px-3 py-2">Source</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map((call) => (
                      <tr key={call.id} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-600">{formatWhen(call.startedAt)}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{call.candidateName}</div>
                          <div className="text-xs text-slate-500">{call.phone}</div>
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {[call.jobTitle, call.companyName].filter(Boolean).join(" · ") || "—"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{call.source || "—"}</td>
                        <td className="px-3 py-2 text-slate-700">
                          {CALL_STATUS_COLUMNS.find(([key]) => key === call.disposition)?.[1] ||
                            call.disposition ||
                            "—"}
                          {call.note ? (
                            <div className="text-xs text-slate-500">{call.note}</div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {callsLoading && <p className="mt-2 text-sm text-slate-500">Loading calls…</p>}
            {callsHasMore && !callsLoading && (
              <button
                type="button"
                onClick={() => openCalls(statusPerson, callsPage + 1)}
                className="mt-3 text-sm font-medium text-[#C2412A]"
              >
                Load more
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export const HotlineAnalytics: React.FC = () => {
  const [fromDay, setFromDay] = useState(todayInput);
  const [toDay, setToDay] = useState(todayInput);
  const [items, setItems] = useState<HotlineStatusCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [listKey, setListKey] = useState<string | null>(null);
  const [calls, setCalls] = useState<HotlineTrackedCall[]>([]);
  const [callsPage, setCallsPage] = useState(1);
  const [callsTotal, setCallsTotal] = useState(0);
  const [callsLoading, setCallsLoading] = useState(false);

  const load = useCallback(async (from: string, to: string) => {
    const range = rangeFromInputs(from, to);
    if (new Date(range.to) <= new Date(range.from)) {
      toast.error("The end date has to be on or after the start date.");
      return;
    }
    setLoading(true);
    setListKey(null);
    setCalls([]);
    setCallsTotal(0);
    try {
      const result = await getHotlineStatusSummary(range.from, range.to, 1, "", true);
      setItems(result.items);
    } catch (error) {
      toast.error(errorMessage(error));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(todayInput(), todayInput());
  }, [load]);

  const totals = useMemo(() => {
    const statuses: Record<string, number> = {};
    let attempts = 0;
    for (const item of items) {
      attempts += item.attempts;
      for (const [status, count] of Object.entries(item.statuses)) {
        statuses[status] = (statuses[status] ?? 0) + count;
      }
    }
    return { attempts, statuses };
  }, [items]);

  const openList = async (key: string, page: number) => {
    const range = rangeFromInputs(fromDay, toDay);
    setListKey(key);
    setCallsLoading(true);
    try {
      const result = await getHotlineTeamCalls({
        from: range.from,
        to: range.to,
        page,
        disposition: key === "attempts" ? undefined : key,
      });
      setCalls(result.items);
      setCallsPage(result.page);
      setCallsTotal(result.total);
    } catch (error) {
      toast.error(errorMessage(error));
      setCalls([]);
      setCallsTotal(0);
    } finally {
      setCallsLoading(false);
    }
  };

  const listLabel =
    listKey === "attempts"
      ? "Attempts"
      : CALL_STATUS_COLUMNS.find(([key]) => key === listKey)?.[1] || "Calls";

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-end justify-between gap-3 px-4 py-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">Team analytics</h2>
          <p className="mt-1 text-xs text-slate-500">
            Team totals for this account. Press a total to see those calls.
          </p>
        </div>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            load(fromDay, toDay);
          }}
        >
          <label className="text-xs text-slate-500">
            From
            <input
              type="date"
              value={fromDay}
              onChange={(event) => setFromDay(event.target.value)}
              className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
          <label className="text-xs text-slate-500">
            To
            <input
              type="date"
              value={toDay}
              onChange={(event) => setToDay(event.target.value)}
              className="mt-1 block rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-900"
            />
          </label>
          <button
            type="submit"
            className="rounded-lg bg-[#EC6E51] px-3 py-1.5 text-sm font-medium text-white"
          >
            Apply
          </button>
        </form>
      </div>
      <div className="border-t border-slate-200 p-4">
        {loading ? (
          <p className="py-8 text-center text-sm text-slate-500">Loading analytics…</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">No one is in this team yet.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <button
              type="button"
              onClick={() => openList("attempts", 1)}
              className={`rounded-xl border px-4 py-3 text-left ${
                listKey === "attempts"
                  ? "border-[#EC6E51] bg-[#FFF1EC]"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100"
              }`}
            >
              <div className="text-xs uppercase tracking-wide text-slate-500">Attempts</div>
              <div className="mt-1 text-2xl font-semibold text-slate-900">{totals.attempts}</div>
            </button>
            {CALL_STATUS_COLUMNS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => openList(key, 1)}
                className={`rounded-xl border px-4 py-3 text-left ${
                  listKey === key
                    ? "border-[#EC6E51] bg-[#FFF1EC]"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
              >
                <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-1 text-2xl font-semibold text-slate-900">{totals.statuses[key] ?? 0}</div>
              </button>
            ))}
          </div>
        )}
      </div>
      {listKey && (
        <div className="border-t border-slate-200 px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-800">{listLabel}</h3>
            <TablePager page={callsPage} total={callsTotal} onPage={(page) => openList(listKey, page)} />
          </div>
          {callsLoading ? (
            <p className="mt-3 text-sm text-slate-500">Loading calls…</p>
          ) : calls.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">No calls in this period.</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Candidate</th>
                    <th className="px-3 py-2">Recruiter</th>
                    <th className="px-3 py-2">Job</th>
                    <th className="px-3 py-2">Source</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <tr key={call.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-600">{formatWhen(call.startedAt)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{call.candidateName}</div>
                        <div className="text-xs text-slate-500">{call.phone}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{call.recruiterName || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {[call.jobTitle, call.companyName].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{call.source || "—"}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {CALL_STATUS_COLUMNS.find(([key]) => key === call.disposition)?.[1] ||
                          call.disposition ||
                          "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default HotlineTracking;
